import { getFrappeInterceptNewCreateActionMap } from '@/constants/frappeInterceptNewCreateActionMap'
import { listCreateRouteMap } from '@/constants/listCreateRouteMap.ts'
import { RelationGroupType, RelationType } from '@/constants/relations'
import router from '@/routers/routerRoot.ts'
import { useAlertManagerStore } from '@/stores/alertManager'
import { useFrappeNestCustomAPIStore } from '@/stores/apis/frappeNestCustomAPI'
import {
  type PrefillMetaData,
  useCreateRelationSlideInStore,
} from '@/stores/createRelationSlideInStore'
import { useModalManagerStore } from '@/stores/modalManager'
import {
  isAlertAction,
  isModalAction,
  isRedirectAction,
  isSlideInAction,
  type SlideInAction,
} from '@/types/internal/frappeActionTypes'
import { isRefRedirectAction } from '@/types/internal/frappeActionTypes.ts'
import { openBankAccountPaymentCodeBlockAlert } from '@/utilities/bankAccountPaymentCodeGuard'
import { routerRedirectWithBackRoute } from '@/utilities/routers.ts'

interface FrappeInterceptNewCreateMsgPayload {
  clickedFieldName: keyof ReturnType<typeof getFrappeInterceptNewCreateActionMap>
  parentDoctype?: ParentDoctype | undefined
  paymentCodeEnabled?: boolean | undefined
  targetDisabled?: boolean | undefined
  targetName?: string | undefined
}

// Bank account actions that remove/disable an account and therefore must be
// blocked when they target the company's default account while the payment QR
// code feature is enabled.
const PAYMENT_CODE_GUARDED_REMOVE_ACTIONS = ['delete_bank_account', 'deactivate_bank_account']

interface ParentDoctype {
  docstatus: 0 | 1 | 2
  doctype: string
  name: string
  owner: string
  transaction_date: string
  company?: string
  customer?: string
  lead?: string
  opportunity?: string
  party?: string
  party_name?: string
  party_type?: string
  quotation_to?: string
  supplier?: string
  tw_direct_delivery_customer?: string
  tw_is_direct_delivery_to_customer?: 0 | 1
}

const isFrappeInterceptFieldName = (
  key: string,
): key is keyof ReturnType<typeof getFrappeInterceptNewCreateActionMap> => {
  return key in getFrappeInterceptNewCreateActionMap()
}

const isTruthyFrappeFlag = (value: unknown): boolean => {
  return value === true || value === 'true' || value === 1 || value === '1'
}

const buildOrganizationPrefillMetaData = async (
  organizationName: string,
): Promise<PrefillMetaData | undefined> => {
  const frappeNestCustomAPIStore = useFrappeNestCustomAPIStore()

  const organizationList = await frappeNestCustomAPIStore.getOrganizationList({
    fields: [
      'name',
      'doctype',
      'organization_name',
      'image',
      'tw_responsible_employee',
      'tw_rating',
    ],
    filters: { AND: [['name', '=', organizationName]], OR: [] },
    limit: 1,
    sub_type: RelationType.all,
    type: RelationGroupType.organizations,
  })

  const [organization] = organizationList

  return organization
    ? {
        sourceDocname: organization.name,
        sourceDoctype: organization.doctype,
        sourceEntityName: organization.organization_name,
        sourceEntityType: 'organization',
        sourceImage: organization.image,
        tw_responsible_employee: organization.tw_responsible_employee?.name ?? null,
        ...(organization.tw_rating ? { tw_rating: organization.tw_rating } : {}),
      }
    : undefined
}

// Opens the create-relation slide-in for an intercepted link field
const handleSlideInCreateAction = async (
  config: SlideInAction,
  linkName: string | undefined,
): Promise<void> => {
  const createRelationSlideInStore = useCreateRelationSlideInStore()

  const prefillMetaData =
    config.relationGroupType === RelationGroupType.persons && linkName
      ? await buildOrganizationPrefillMetaData(linkName)
      : undefined

  const wasOpened = createRelationSlideInStore.openCreateRelationOverlay(config.relationGroupType, {
    initialPersonOrganisationRelationLinkName: linkName,
    organizationRelationType: config.organizationRelationType,
    prefillMetaData,
  })

  if (!wasOpened) {
    return
  }
  if (!linkName) {
    console.warn('[Intercept] Initialdaten für Relation Link sind unvollständig:', {
      linkName,
    })
  }
}

// A dynamic org picker (e.g. the Buying Center) passes the concrete org type via
// create_doctype. RelationType values equal the doctype strings, so it can be used
// directly to open/preselect the right org create form.
const resolveSlideInConfig = (config: SlideInAction, createDoctype: unknown): SlideInAction => {
  if (
    config.relationGroupType === RelationGroupType.organizations &&
    typeof createDoctype === 'string' &&
    createDoctype
  ) {
    return { ...config, organizationRelationType: createDoctype }
  }

  return config
}

const handleFrappeInterceptNewCreateActions = async (event: MessageEvent): Promise<void> => {
  const msg = event.data
  if (msg?.type !== 'tw-create-new-event-information') return

  const {
    clicked_field_name,
    create_doctype,
    linkName,
    parent_doctype,
    payment_code_enabled,
    reference_doctype,
    target_disabled,
    target_is_default,
    target_name,
    targetDisabled,
  } = msg.payload

  if (!isFrappeInterceptFieldName(clicked_field_name)) {
    console.warn('[Intercept] No Action defined for:', clicked_field_name)
    return
  }

  // Hard-block removing/deactivating the company's default bank account while the
  // payment QR code feature is enabled (the QR code on print formats relies on it).
  if (
    PAYMENT_CODE_GUARDED_REMOVE_ACTIONS.includes(clicked_field_name) &&
    isTruthyFrappeFlag(payment_code_enabled) &&
    isTruthyFrappeFlag(target_is_default)
  ) {
    openBankAccountPaymentCodeBlockAlert()
    return
  }

  const frappeInterceptNewCreateActionMap = getFrappeInterceptNewCreateActionMap()
  const config = frappeInterceptNewCreateActionMap[clicked_field_name]

  if (!config) {
    console.warn('[Intercept] config not found for:', clicked_field_name)
    return
  }

  const modalManager = useModalManagerStore()
  const alertManager = useAlertManagerStore()

  if (isModalAction(config)) {
    modalManager.openModal(config.component, config.title, {
      frappeInterceptNewCreateMsgPayload: {
        clickedFieldName: clicked_field_name,
        parentDoctype: parent_doctype,
        paymentCodeEnabled: isTruthyFrappeFlag(payment_code_enabled),
        targetName: target_name,
      },
    })
  } else if (isAlertAction(config)) {
    if (!target_name) {
      console.warn('[Intercept] target_name is missing Alert-Action:', clicked_field_name)
      return
    }

    alertManager.openAlert({
      alertType: config.alertType,
      cancelLabel: config.cancelLabel,
      ctaLabel: config.ctaLabel,
      message: config.message,
      messageTitle: config.messageTitle,
      onSubmit: async () => {
        await config.onSubmit(target_name, {
          targetDisabled: isTruthyFrappeFlag(target_disabled ?? targetDisabled),
        })
      },
    })
  } else if (isRedirectAction(config)) {
    if (clicked_field_name === 'task' && parent_doctype?.name) {
      await router.push({
        name: config.route,
        query: {
          docname: parent_doctype.name,
          doctype: 'Task',
        },
      })
    } else {
      await routerRedirectWithBackRoute(router, config.route)
    }
  } else if (isRefRedirectAction(config)) {
    if (reference_doctype in listCreateRouteMap) {
      await routerRedirectWithBackRoute(router, listCreateRouteMap[reference_doctype])
    }
  } else if (isSlideInAction(config)) {
    await handleSlideInCreateAction(resolveSlideInConfig(config, create_doctype), linkName)
  }
}

export { type FrappeInterceptNewCreateMsgPayload, handleFrappeInterceptNewCreateActions }
